import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useGetContactCollectionByMagicLinkQuery, useCreateContactsWithoutTagsMutation, useGetTelemetryDataForContactCollectionGuestQuery } from '@graphql/generated';
import type { VisualType } from './shared/constants';
import { getImageSource } from './shared/utils/getImageSource';
import { useTranslation, useRouterHelper } from '@shared/core';
import { useContactCollectorGuestTelemetry } from './ContactCollectorGuest.telemetry';
import { contactCollectorGuestRoutePaths } from '.';
import type { ContactInputFields, Contacts, Context } from './shared/types';

export type { ContactInputFields, Contacts, Context };

const contactInitialValue: ContactInputFields = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  region: '',
  country: 'United States',
  countryCode: 'US',
  validated: false,
};

export const newMember = {
  ...contactInitialValue,
  address1: ' ',
  postalCode: ' ',
  city: ' ',
  region: ' ',
};

const initialValues: Contacts = {
  contacts: [contactInitialValue],
};

const yupSchema = (tRequired: string, tInvalidEmail: string) =>
  Yup.object({
    contacts: Yup.array()
      .min(1)
      .of(
        Yup.object<ContactInputFields>({
          firstName: Yup.string().required(tRequired),
          lastName: Yup.string().required(tRequired),
          phoneNumber: Yup.string(),
          email: Yup.string().email(tInvalidEmail),
          address1: Yup.string().required(tRequired),
          address2: Yup.string(),
          postalCode: Yup.string().required(tRequired),
          city: Yup.string().required(tRequired),
          region: Yup.string().required(tRequired),
          country: Yup.string(),
          countryCode: Yup.string(),
          validated: Yup.boolean(),
        }),
      ),
  });

export const useContactCollectorGuestController = (magicLinkId: string) => {
  const [e164PhoneNumber, setE164PhoneNumber] = useState<{ [key: number]: string }>({ 0: '' });
  const [context, setContext] = useState<Context>({
    coupleNames: '',
    eventDisplayName: '',
    date: '',
    eventDateDisplayEnabled: false,
    website: '',
    websiteLinkEnabled: false,
    fontFamily: 'PT Serif',
    fontTextTransform: 'none',
    mediaType: '',
    mediaSource: '',
    firebaseId: '',
  });
  const { trackError, buttonInteracted } = useContactCollectorGuestTelemetry();
  const routeHelpers = useRouterHelper();
  const { t } = useTranslation('contactCollector');
  const tAdmin = t('admin');
  const { t2 } = useTranslation('ssrServer');
  const tMetatags = t2('metatags');
  const { value: contactCollectionFeatureEnabled, loading: isFeatureFlagLoading } = useFeatureValue('contactCollectionEnabled');
  const { data, error } = useGetContactCollectionByMagicLinkQuery({
    variables: {
      magicLink: magicLinkId,
    },
    batchMode: 'fast',
    ssr: true,
    fetchPolicy: 'cache-first',
    onError: error => trackError('Query', error),
  });

  const eventId = data?.getContactCollectionByMagicLink?.eventId;
  const isContactCollectionInactive = !data?.getContactCollectionByMagicLink?.active;

  const { data: telemetryQueryData, loading: isTelemetryQueryLoading } = useGetTelemetryDataForContactCollectionGuestQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      eventId: eventId!,
    },
    skip: !eventId,
    onError: error => trackError('Query', error),
  });

  const ogImage = getImageSource(data?.getContactCollectionByMagicLink?.mediaType as VisualType, data?.getContactCollectionByMagicLink?.mediaSource ?? '');
  const ogTitle = data?.getContactCollectionByMagicLink?.message || tMetatags.title;
  const ogUrl = `https://withjoy.com/contact/${magicLinkId}`;

  const [createContactsMutation, { loading: isCreateContactsMutationLoading, error: createContactsMutationError }] = useCreateContactsWithoutTagsMutation();

  const formik = useFormik<Contacts>({
    initialValues,
    validationSchema: yupSchema(tAdmin.required(), tAdmin.invalidEmail()),
    validateOnMount: true,
    onSubmit: async values => {
      try {
        const contactCollectionId = data?.getContactCollectionByMagicLink?.id;

        if (!contactCollectionId) {
          throw Error('No Collection Id provided');
        }

        const mutationData = values.contacts.map((contact, index) => ({
          address:
            // we do not want to save addresses from household members which are not the head of the household
            index === 0
              ? {
                  address1: contact.address1,
                  address2: contact.address2,
                  city: contact.city,
                  country: contact.country,
                  countryCode: contact.countryCode,
                  postalCode: contact.postalCode,
                  region: contact.region,
                  validated: contact.validated,
                }
              : undefined,
          contact: {
            contactCollectionId,
            firebaseEventId: context.firebaseId,
            eventId,
            email: ((contact.email || values.contacts[0].email) ?? '').trim(),
            firstName: (contact.firstName ?? '').trim(),
            lastName: (contact.lastName ?? '').trim(),
            party: values.contacts.length > 1 ? `${contactCollectionId}-${values.contacts[0].firstName}-${values.contacts[0].lastName}` : undefined,
          },
          phoneNumbers: contact.phoneNumber
            ? [
                {
                  allowSMS: true,
                  mobile: true,
                  number: contact.phoneNumber.trim(),
                },
              ]
            : undefined,
        }));
        await createContactsMutation({ variables: { data: mutationData } });

        // Track successful form submission
        buttonInteracted({
          buttonType: 'save',
          element: 'submitContactForm',
          extraInfo: {
            url: `https://withjoy.com/contact/${magicLinkId}`,
            eventHandle: telemetryQueryData?.eventById?.website ?? '',
            guestFirstName: values.contacts[0].firstName,
            guestLastName: values.contacts[0].lastName,
            guestEmail: values.contacts[0].email,
          },
        });

        setE164PhoneNumber({ 0: '' });
        routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.done.path);
      } catch (error) {
        trackError('Mutation', error);
      }
    },
  });

  return {
    tAdmin,
    showErrorPage: !contactCollectionFeatureEnabled || !!error,
    isContactCollectionInactive,
    eventHandle: telemetryQueryData?.eventById?.website ?? '',
    isLoading: isFeatureFlagLoading || isTelemetryQueryLoading,
    data,
    noData: data?.getContactCollectionByMagicLink === null,
    ogImage,
    ogTitle,
    ogUrl,
    formik,
    context,
    setContext,
    isMutationLoading: isCreateContactsMutationLoading,
    hasMutationError: !!createContactsMutationError,
    telemetryData: {
      postgresEventId: telemetryQueryData?.eventById?.id ?? '',
      firebaseEventId: telemetryQueryData?.eventById?.firebaseId ?? '',
      joyEventType: telemetryQueryData?.eventById?.info.eventType ?? '',
    },
    e164PhoneNumber,
    setE164PhoneNumber,
  };
};
